import { useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import type { Card, CardStatus } from "@feed/shared";
import { relativeTime } from "../lib/relative";
import { blockStyle } from "../lib/types";
import { useSheetFocus } from "../lib/useSheetFocus";
import { itemsText } from "./CardBlock";
import { BookmarkIcon, CloseIcon, ExternalIcon } from "./icons";

interface DetailSheetProps {
  card: Card;
  onClose: () => void;
  onSetStatus: (status: CardStatus) => void;
}

// Links only ever open if they are http(s): the server already refuses anything else,
// and this is the second lock on the same door.
function safeHref(url: string): string | null {
  try {
    const protocol = new URL(url).protocol;
    return protocol === "http:" || protocol === "https:" ? url : null;
  } catch {
    return null;
  }
}

export function DetailSheet({ card, onClose, onSetStatus }: DetailSheetProps) {
  const { t, i18n } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const close = useCallback(onClose, [onClose]);
  useSheetFocus(true, ref, close);
  const saved = card.status === "saved";

  return (
    <div className="sheet-backdrop" onClick={onClose}>
      <div
        className={`sheet block-${blockStyle(card.type)}`}
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-label={t("sheet.label")}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="sheet-head">
          <div className="sheet-grip" aria-hidden="true" />
          <div className="sheet-meta">
            <span className="badge" dir="auto">
              {card.type}
            </span>
            <span className="sheet-count">
              {itemsText(t, card.items.length)} · {relativeTime(card.created_at, new Date(), i18n.language)}
            </span>
            <button type="button" className="sheet-close" onClick={onClose} autoFocus>
              <span>{t("common.close")}</span>
              <CloseIcon />
            </button>
          </div>
          <p className="sheet-summary" dir="auto">
            {card.summary}
          </p>
          <div className="sheet-actions">
            <button type="button" className="pill pill-solid" onClick={() => onSetStatus(saved ? "read" : "saved")}>
              <BookmarkIcon filled={saved} />
              {saved ? t("saved.unsave") : t("sheet.save")}
            </button>
            <button type="button" className="pill pill-outline" onClick={() => onSetStatus("dismissed")}>
              <CloseIcon />
              {t("sheet.dismiss")}
            </button>
          </div>
        </div>
        <ul className="sheet-items" aria-label={t("sheet.itemsList")}>
          {card.items.map((item, index) => {
            const href = safeHref(item.url);
            const body = (
              <>
                <span className="item-text">
                  <span className="item-title" dir="auto">
                    {item.title}
                  </span>
                  {item.subtitle && (
                    <span className="item-subtitle" dir="auto">
                      {item.subtitle}
                    </span>
                  )}
                </span>
                {href && (
                  <>
                    <span className="visually-hidden">{t("sheet.opensNew")}</span>
                    <ExternalIcon />
                  </>
                )}
              </>
            );
            return (
              <li key={`${index}-${item.url}`}>
                {href ? (
                  <a className="item" href={href} target="_blank" rel="noopener noreferrer">
                    {body}
                  </a>
                ) : (
                  <span className="item">{body}</span>
                )}
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
